import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { EsignatureProvider } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { EsignatureDocumentStatus } from "../../../../api/graphql";
import { ShowDateTime } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type EsignatureDocumentViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  externalIdValue?: { displayId: string; id: string } | null;
  externalId: string;
  providerOptions: { id: string; displayId: string }[];
  provider: EsignatureProvider;
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  statusOptions: { id: string; displayId: string }[];
  status: EsignatureDocumentStatus;
  sentAtValue?: { displayId: string; id: string } | null;
  sentAt: Date | null;
  completedAtValue?: { displayId: string; id: string } | null;
  completedAt: Date | null;
  signedDocumentFiles: CrudRows;
  signedDocumentViewAction?: IMenuAction[];
  sourceEntityTypeValue?: { displayId: string; id: string } | null;
  sourceEntityType: string;
  sourceEntityIdValue?: { displayId: string; id: string } | null;
  sourceEntityId: string;
  recipientsRows: CrudRows;
  recipientsVisibleColumns: GridColumn[];
  recipientsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
};

export const EsignatureDocumentViewForm: React.FC<EsignatureDocumentViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      externalId = "",
      providerOptions = [],
      provider = "" as EsignatureProvider,
      name = "",
      statusOptions = [],
      status = "" as EsignatureDocumentStatus,
      sentAt = null,
      completedAt = null,
      signedDocumentFiles,
      signedDocumentViewAction,
      sourceEntityType = "",
      sourceEntityId = "",
      recipientsRows,
      recipientsVisibleColumns,
      recipientsSetColumnWiths,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupEsignatureDocumentLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={externalId}
                  label={langStore.EsignatureDocumentExternalIdLabel}
                />
                <ShowString
                  value={
                    provider
                      ? providerOptions.find(
                          (o: { id: string }) => o.id === provider
                        )?.displayId || provider
                      : ""
                  }
                  label={langStore.EsignatureDocumentProviderLabel}
                />
                <ShowMultiLine
                  value={name}
                  label={langStore.EsignatureDocumentNameLabel}
                />
                <ShowString
                  value={
                    status
                      ? statusOptions.find(
                          (o: { id: string }) => o.id === status
                        )?.displayId || status
                      : ""
                  }
                  label={langStore.EsignatureDocumentStatusLabel}
                />
                <ShowDateTime
                  value={sentAt}
                  label={langStore.EsignatureDocumentSentAtLabel}
                />
                <ShowDateTime
                  value={completedAt}
                  label={langStore.EsignatureDocumentCompletedAtLabel}
                />
                <ShowFilesCompact
                  rows={signedDocumentFiles}
                  showLabel={true}
                  label={langStore.EsignatureDocumentSignedDocumentLabel}
                  menuActions={signedDocumentViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />

                <ShowMultiLine
                  value={sourceEntityType}
                  label={langStore.EsignatureDocumentSourceEntityTypeLabel}
                />
                <ShowMultiLine
                  value={sourceEntityId}
                  label={langStore.EsignatureDocumentSourceEntityIdLabel}
                />

                <ShowItemsCompact
                  rows={recipientsRows}
                  showLabel={true}
                  label={"Recipients"}
                  columns={recipientsVisibleColumns}
                  setColumnWidths={recipientsSetColumnWiths}
                  entityName="EsignatureRecipient"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("esignatureRecipient", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
